import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import Link from 'next/link';
import { withRouter } from 'next/router';

import { openSignUpModalWithRedirect } from '../../redux/modals';

import track from '../../hocs/tracking';

import TrackingContext from '../../tracking/TrackingContext';

import createLinkObject from './createLinkObject';
import isExternalLink from './isExternalLink';
import parameterize from '../parameterize';
import { handleBeforeRedirect } from '../handleBeforeRedirect';
import { isLoggedIn } from '../selectors/account';

const TrackedLink = track(styled.a`
  cursor: pointer;
`);
TrackedLink.displayName = 'TrackedLink';

const TrackedExternalLink = track(styled.a`
  cursor: pointer;
`);
TrackedExternalLink.displayName = 'TrackedExternalLink';

class HandOffLink extends Component {
  /**
   * propTypes
   * @property {bool}   authRequired  Whether this link should trigger the auth modal, defaults to false.
   * @property {any}    children      The message to display in the box (may contain links).
   * @property {any}    className     A string denoting which classes to apply to this component.
   * @property {array}  preRedirects  An array of functions which will be executed before the redirect happens.
   * @property {string} href          The location at which you want to go to, could be external.
   * @property {string} identifier    A string to scope tracking information.
   * @property {bool}   isLoggedIn    A boolean denoting whether the user is logged in or not.
   * @property {func}   onClick       A function to fire when the user clicks the element.
   * @property {func}   onMouseEnter  A function to fire when the user hovers the element. Ensures it doesn't disppear.
   * @property {func}   onMouseLeave  A function to fire when the user blurs the element. Ensures it doesn't disappear.
   * @property {bool}   preventLinkTracking   A boolean denoting whether a standard link click tracking call should be sent.
   */

  static propTypes = {
    authRequired: PropTypes.bool.isRequired,
    children: PropTypes.any.isRequired,
    className: PropTypes.string,
    preRedirects: PropTypes.arrayOf(PropTypes.func),
    href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    identifier: PropTypes.string,
    isLoggedIn: PropTypes.bool.isRequired,
    onClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    preventLinkTracking: PropTypes.bool,
    trackAs: PropTypes.string,
  };

  static defaultProps = {
    authRequired: false,
    preRedirects: [],
    preventLinkTracking: false,
  };

  handleLinkClick = event => {
    const {
      authRequired,
      href,
      isLoggedIn,
      onClick,
      preRedirects,
      onUnauthenticatedClick,
    } = this.props;

    if (onClick) {
      onClick(event);
    }

    if (authRequired && !isLoggedIn) {
      // Don't navigate and display the login/signup modal.
      // We won't get preRedirects but this has been accepted as a trade-off
      event.preventDefault();
      onUnauthenticatedClick(event.currentTarget.href);
    } else {
      handleBeforeRedirect(preRedirects, 50);
    }
  };

  render() {
    const {
      authRequired,
      children,
      className,
      href,
      identifier,
      isLoggedIn = false,
      onMouseEnter,
      onMouseLeave,
      onUnauthenticatedClick,
      // Currently, preRedirects update transient visual state for components which is discarded on refresh.
      // It should not be used for actions critical to app state or the health of the platform.
      preRedirects,
      router,
      scroll = true,
      target,
      preventLinkTracking,
      trackAs,
    } = this.props;
    let linkObject = {
      href,
    };

    // If an object, its intended to only modify the query.  Merge that query and use it as the URL.
    if (typeof href === 'object') {
      const combinedQuery = { ...router.query, ...href.query };
      delete combinedQuery.slug;
      const asPath = `${router.pathname}?${parameterize(combinedQuery)}`;
      linkObject.href = {
        pathname: combinedQuery.pathname || router.pathname,
        query: combinedQuery,
      };
      linkObject.as = asPath;
      linkObject.shallow = true;
    }

    if (isExternalLink(href)) {
      return (
        <TrackingContext.Consumer>
          {additionalTrackingProps => (
            <TrackedExternalLink
              href={href}
              className={className}
              identifier={identifier}
              target={target}
              onClick={this.handleLinkClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              additionalTrackingProps={additionalTrackingProps}
              preventLinkTracking={preventLinkTracking}
              trackAs={trackAs}
            >
              {children}
            </TrackedExternalLink>
          )}
        </TrackingContext.Consumer>
      );
    } else {
      let linkObject = createLinkObject(href, router);
      return (
        <TrackingContext.Consumer>
          {additionalTrackingProps => (
            <Link {...linkObject} scroll={scroll} passHref>
              <TrackedLink
                href={linkObject.href}
                className={className}
                identifier={identifier}
                target={target}
                onClick={this.handleLinkClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                additionalTrackingProps={additionalTrackingProps}
                preventLinkTracking={preventLinkTracking}
                trackAs={trackAs}
              >
                {children}
              </TrackedLink>
            </Link>
          )}
        </TrackingContext.Consumer>
      );
    }
  }
}

const mapStateToProps = ({ account }) => ({
  isLoggedIn: isLoggedIn(account),
});

const mapDispatchToProps = {
  onUnauthenticatedClick: openSignUpModalWithRedirect,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(HandOffLink),
);
